import { useState, useEffect } from "react";
import { Plus, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { WorkflowEditorDialog } from "@/components/dashboard/WorkflowEditorDialog";
import { useWorkflows } from "@/hooks/useWorkflows";
import { WorkflowFormTriggers } from "@/components/forms/WorkflowFormTriggers";

type WorkflowType = 'retail' | 'insurance' | 'commercial';

export default function WorkflowAutomation() {
  const [orgId, setOrgId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<WorkflowType>('retail');
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const retailWorkflow = useWorkflows(orgId, 'retail');
  const insuranceWorkflow = useWorkflows(orgId, 'insurance');
  const commercialWorkflow = useWorkflows(orgId, 'commercial');

  const workflows = {
    retail: retailWorkflow,
    insurance: insuranceWorkflow,
    commercial: commercialWorkflow,
  };

  const currentWorkflow = workflows[selectedType];

  useEffect(() => {
    loadOrgId();
  }, []);

  const loadOrgId = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", user.id)
        .single();

      if (profile?.org_id) {
        setOrgId(profile.org_id);
      }
    } catch (error) {
      console.error("Error loading org:", error);
      toast.error("Failed to load organization");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">Loading workflows...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Workflow Control Center</h1>
          <p className="text-muted-foreground mt-1">
            Define stages, automations, and form triggers that control how jobs move through your pipeline
          </p>
        </div>
      </div>

      <Card className="border-2 border-primary/20 bg-gradient-to-br from-background to-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            How It Works
          </CardTitle>
          <CardDescription>
            This is the central hub that controls your entire job workflow
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex gap-3">
            <Badge variant="outline" className="shrink-0">1</Badge>
            <p><strong>Define Stages:</strong> Create the steps jobs go through (e.g., Lead → Contacted → Scheduled)</p>
          </div>
          <div className="flex gap-3">
            <Badge variant="outline" className="shrink-0">2</Badge>
            <p><strong>Set Automations:</strong> Configure what happens at each stage (send emails, create tasks, attach forms)</p>
          </div>
          <div className="flex gap-3">
            <Badge variant="outline" className="shrink-0">3</Badge>
            <p><strong>Jobs Follow Workflows:</strong> When jobs move through stages, your automations fire automatically</p>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="stages" className="space-y-4">
        <TabsList>
          <TabsTrigger value="stages">Stages</TabsTrigger>
          <TabsTrigger value="forms">Forms</TabsTrigger>
          <TabsTrigger value="automations">Automations</TabsTrigger>
        </TabsList>

        <TabsContent value="stages" className="space-y-4">
          <Tabs value={selectedType} onValueChange={(v) => setSelectedType(v as WorkflowType)} className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="retail">
                Retail/Standard
                {retailWorkflow.workflow && <Badge variant="outline" className="ml-2">{retailWorkflow.workflow.stages.length} stages</Badge>}
              </TabsTrigger>
              <TabsTrigger value="insurance">
                Insurance
                {insuranceWorkflow.workflow && <Badge variant="outline" className="ml-2">{insuranceWorkflow.workflow.stages.length} stages</Badge>}
              </TabsTrigger>
              <TabsTrigger value="commercial">
                Commercial
                {commercialWorkflow.workflow && <Badge variant="outline" className="ml-2">{commercialWorkflow.workflow.stages.length} stages</Badge>}
              </TabsTrigger>
            </TabsList>

            {(['retail', 'insurance', 'commercial'] as WorkflowType[]).map((type) => {
              const wf = workflows[type];
              return (
                <TabsContent key={type} value={type} className="space-y-4">
                  {wf.loading ? (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <div className="text-muted-foreground">Loading workflow...</div>
                      </CardContent>
                    </Card>
                  ) : wf.workflow ? (
                    <Card>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="flex items-center gap-2">
                              {wf.workflow.name}
                            </CardTitle>
                            <CardDescription className="mt-1">
                              {wf.workflow.description || 'Industry-standard workflow for ' + type + ' projects'}
                            </CardDescription>
                          </div>
                          <Button onClick={() => setIsEditorOpen(true)}>
                            <Settings className="w-4 h-4 mr-2" />
                            Configure
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <h4 className="font-semibold mb-3 text-sm text-muted-foreground uppercase tracking-wider">
                            Workflow Stages
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {wf.workflow.stages.map((stage, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-card"
                              >
                                <div
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: stage.color }}
                                />
                                <span className="text-sm font-medium">{stage.stage_name}</span>
                                <Badge variant="outline" className="text-xs">{index + 1}</Badge>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="pt-4 border-t">
                          <p className="text-sm text-muted-foreground">
                            <strong>Active Jobs:</strong> All {type} jobs will use this workflow and progress through these stages.
                            Automations configured for each stage will trigger automatically.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <p className="text-muted-foreground">No workflow configured</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        </TabsContent>

        <TabsContent value="forms" className="space-y-4">
          {currentWorkflow.workflow ? (
            <WorkflowFormTriggers workflowId={currentWorkflow.workflow.id} />
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">Configure a workflow first to manage form triggers</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="automations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Automation Actions</CardTitle>
              <CardDescription>Configure automated actions for each workflow stage</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Coming soon: Email notifications, task creation, and webhook triggers
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {currentWorkflow.workflow && (
        <WorkflowEditorDialog
          open={isEditorOpen}
          onOpenChange={setIsEditorOpen}
          workflow={currentWorkflow.workflow}
          onSave={currentWorkflow.saveWorkflow}
          onReset={currentWorkflow.resetToDefault}
        />
      )}
    </div>
  );
}
